import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { bookingFormActions } from './booking-form.reducer';
import { BookingDurationType } from './booking-form.state';
import { selectBookingFormData } from '../reducers';
import { BookingsService } from '../../services/bookings.service';
import { DatetimeHelper } from '../../helpers/datetime.helper';
import { parseISO } from 'date-fns';
import { BookingType } from '../../enums/booking-type';
import { GetBookingCostsRequest } from '../../requests/get-booking-costs.request';
import { ToastService } from '../../services/toast.service';

@Injectable()
export class BookingFormEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private bookingsService = inject(BookingsService);
  private toastService = inject(ToastService);

  // Effect pour charger les coûts de la réservation
  findBookingCosts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingFormActions.findBookingCosts),
      withLatestFrom(this.store.select(selectBookingFormData)),
      switchMap(([_, booking]) => {
        // Vérifier que les données minimales sont présentes
        const isMultipleDates = booking.booking_type === BookingType.MultipleDates;
        const hasRequiredData = isMultipleDates
          ? booking.multiple_dates && booking.multiple_dates.length > 0
          : booking.start;

        if (!hasRequiredData || !booking.room_id) {
          return of(bookingFormActions.findBookingCostsFailure({}));
        }

        // Construire le payload de base
        const basePayload: Partial<GetBookingCostsRequest> = {
          room_id: booking.room_id,
          complementary_rooms_ids: booking.complementary_rooms_ids ?? [],
          booking_type: booking.booking_type,
          room_amenities_ids: booking.room_amenities_ids ?? [],
          booking_options_ids: booking.booking_options_ids ?? [],
          amenities_extras_ids: booking.amenities_extras_ids ?? [],
          rate_id: booking.rate_id,
          coupon_code: booking.coupon_code ?? null,
          booking_id: booking.id ?? null,
        };

        // Construire le payload selon le type de réservation
        let payload: GetBookingCostsRequest;

        if (isMultipleDates) {
          // Cas MultipleDates : utiliser multiple_dates et formater les dates
          payload = {
            ...basePayload,
            multiple_dates: booking.multiple_dates.map((date) => ({
              start: DatetimeHelper.dateToApiFormat(
                typeof date.start === 'string' ? parseISO(date.start) : date.start,
                true,
              ),
              end: DatetimeHelper.dateToApiFormat(
                typeof date.end === 'string' ? parseISO(date.end) : date.end,
                true,
              ),
              all_day: date.all_day,
            })),
          } as GetBookingCostsRequest;
        } else {
          // Cas Single, Consecutive, Recurrence : utiliser start, end, all_day, recurrence
          payload = {
            ...basePayload,
            start: booking.start
              ? DatetimeHelper.dateToApiFormat(
                  typeof booking.start === 'string'
                    ? parseISO(booking.start)
                    : booking.start,
                  true,
                )
              : null,
            end: booking.end
              ? DatetimeHelper.dateToApiFormat(
                  typeof booking.end === 'string'
                    ? parseISO(booking.end)
                    : booking.end,
                  true,
                )
              : null,
            all_day: booking.duration_type !== BookingDurationType.Hourly,
            recurrence: booking.recurrence
              ? {
                  ...booking.recurrence,
                  end: DatetimeHelper.dateToApiFormat(
                    parseISO(booking.recurrence.end),
                    true,
                  ),
                }
              : null,
          } as GetBookingCostsRequest;
        }

        // Appeler le service pour récupérer l'invoice
        return this.bookingsService.getCosts(payload).then(
          (invoice) => bookingFormActions.findBookingCostsSuccess({ invoice }),
          (error) => {
            console.error('Error finding booking costs:', error);
            return bookingFormActions.findBookingCostsFailure({
              error: error?.error?.message || error?.message || 'Erreur lors du calcul des coûts',
            });
          },
        );
      }),
    ),
  );

  // Effect pour valider un coupon avant de l'ajouter à l'état
  validateCoupon$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingFormActions.validateCoupon),
      withLatestFrom(this.store.select(selectBookingFormData)),
      switchMap(([action, booking]) => {
        // Vérifier que les données minimales sont présentes
        const isMultipleDates = booking.booking_type === BookingType.MultipleDates;
        const hasRequiredData = isMultipleDates
          ? booking.multiple_dates && booking.multiple_dates.length > 0
          : booking.start;

        if (!hasRequiredData || !booking.room_id) {
          this.toastService.error('Veuillez d\'abord sélectionner une salle et une date');
          return of(bookingFormActions.validateCouponFailure({ error: 'Missing required data' }));
        }

        // Construire le payload avec le coupon à valider
        const basePayload: Partial<GetBookingCostsRequest> = {
          room_id: booking.room_id,
          complementary_rooms_ids: booking.complementary_rooms_ids ?? [],
          booking_type: booking.booking_type,
          room_amenities_ids: booking.room_amenities_ids ?? [],
          booking_options_ids: booking.booking_options_ids ?? [],
          amenities_extras_ids: booking.amenities_extras_ids ?? [],
          rate_id: booking.rate_id,
          coupon_code: action.couponCode,
          booking_id: booking.id ?? null,
        };

        let payload: GetBookingCostsRequest;

        if (isMultipleDates) {
          payload = {
            ...basePayload,
            multiple_dates: booking.multiple_dates.map((date) => ({
              start: DatetimeHelper.dateToApiFormat(
                typeof date.start === 'string' ? parseISO(date.start) : date.start,
                true,
              ),
              end: DatetimeHelper.dateToApiFormat(
                typeof date.end === 'string' ? parseISO(date.end) : date.end,
                true,
              ),
              all_day: date.all_day,
            })),
          } as GetBookingCostsRequest;
        } else {
          payload = {
            ...basePayload,
            start: booking.start
              ? DatetimeHelper.dateToApiFormat(
                  typeof booking.start === 'string'
                    ? parseISO(booking.start)
                    : booking.start,
                  true,
                )
              : null,
            end: booking.end
              ? DatetimeHelper.dateToApiFormat(
                  typeof booking.end === 'string'
                    ? parseISO(booking.end)
                    : booking.end,
                  true,
                )
              : null,
            all_day: booking.duration_type !== BookingDurationType.Hourly,
            recurrence: booking.recurrence
              ? {
                  ...booking.recurrence,
                  end: DatetimeHelper.dateToApiFormat(
                    parseISO(booking.recurrence.end),
                    true,
                  ),
                }
              : null,
          } as GetBookingCostsRequest;
        }

        // Appeler l'API pour valider le coupon
        return this.bookingsService.getCosts(payload).then(
          (invoice) => {
            this.toastService.success('Code de réduction appliqué');
            return bookingFormActions.validateCouponSuccess({
              couponCode: action.couponCode,
              invoice,
            });
          },
          (error) => {
            console.error('Error validating coupon:', error);
            this.toastService.error('Code de réduction invalide');
            return bookingFormActions.validateCouponFailure({
              error: error?.error?.message || error?.message || 'Invalid coupon',
            });
          },
        );
      }),
    ),
  );
}
